<template>
  <v-container>
    <h1>Loan Product Criteria</h1>
    <router-link
      class="default-btn text"
      :to="{
        name: 'LenderPrivate',
        params: { productId: productId },
      }"
    >See public view</router-link>
    <hr class="header" />

    <div class="alert-wrapper">
      <v-alert
        v-if="alertMessage.message"
        :type="alertMessage.type || 'success'"
        dismissable
        outlined>
        <template #prepend>
          <v-icon
            @click="alertMessage.message = ''">mdi-close</v-icon>
        </template>
        {{ alertMessage.message }}
        <ul v-if="alertMessage.list.length">
          <li v-for="item in alertMessage.list">
            {{ item }}
          </li>
        </ul>
      </v-alert>
    </div>

    <v-card class="product-card">
      <v-card-title>
        <div class="button-group">
          <template v-if="product.published">
            <button
              class="default-btn white inverse"
              @click="updatePublishedStatus(false)">Unpublish</button>
            <button
              class="default-btn blue"
              :disabled="!productUpdated || saving"
              @click="debouncedSave"
            >
              Update
              <v-icon v-if="saving" class="ml-1">mdi-loading mdi-spin</v-icon>
            </button>
          </template>

          <button
            v-else
            class="default-btn blue publish-button"
            :disabled="!product.id"
            @click="updatePublishedStatus(true)">
            Publish
          </button>
        </div>

        <h2>
          {{ product.internalName || product.title || 'Example Product' }}
          <v-icon
            @click="showInternalNameDialog = true">
            mdi-square-edit-outline
          </v-icon>
        </h2>
      </v-card-title>

      <v-card-text>
        <v-tabs
          show-arrows
          @change="activeTab = $event">
          <v-tab
            v-for="(tab, index) in tabs"
            :key="index"
            :ref="`tab-${index}`">
            {{ tab.text }}
          </v-tab>

          <v-tab-item
            v-for="(tab, index) in tabs"
            :key="index">
            <keep-alive>
              <component
                :is="tab.component"
                :product="product"
                @change="changeField" />
            </keep-alive>
          </v-tab-item>
        </v-tabs>
      </v-card-text>

      <v-card-actions>
        <div class="button-group">
          <button
            class="default-btn blue"
            :disabled="saving"
            @click="debouncedSave">
            Save
            <v-icon v-if="saving" class="ml-1">mdi-loading mdi-spin</v-icon>
          </button>
          <button
            v-if="activeTab < tabs.length - 1"
            class="default-btn text"
            @click="advanceTab">
            Next Section
          </button>
        </div>
      </v-card-actions>
    </v-card>

    <DialogInternalName
      v-if="showInternalNameDialog"
      :existingName="product.internalName"
      @cancel="showInternalNameDialog = false"
      @submit="updateInternalName" />
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'

import { debounce } from '@/utils'

import DialogInternalName from '../../components/DialogInternalName'
import SectionDeal from './SectionDeal'
import SectionDisclosures from './SectionDisclosures'
import SectionFeatures from './SectionFeatures'
import SectionInfo from './SectionInfo'
import SectionLoan from './SectionLoan'
import SectionPractice from './SectionPractice'

export default {
  name: 'OrganizationProducts',
  components: {
    DialogInternalName,
  },
  props: {
    organizationId: String,
    productId: String,
  },
  data: () => ({
    editing: false,
    saving: false,
    product: {
      dealSize: {},
    },
    updatedProductFields: {},
    productUpdated: false,
    showInternalNameDialog: false,
    activeTab: 0,
    alertMessage: {
      message: '',
      type: '',
      list: [],
    },
    tabs: [
      {
        text: 'Business Information',
        component: SectionInfo,
      },
      {
        text: 'Deal Type',
        component: SectionDeal,
      },
      {
        text: 'Practice Type',
        component: SectionPractice,
      },
      {
        text: 'Loan Details',
        component: SectionLoan,
      },
      {
        text: 'Features',
        component: SectionFeatures,
      },
      {
        text: 'Disclosures/Bankruptcies',
        component: SectionDisclosures,
      },
    ],
    debouncedSave: () => {},
  }),

  computed: {
    ...mapGetters('feathers', ['apiProducts']),
  },

  watch: {
    activeTab () {
      this.scrollToCardTop()
    },
  },

  async created () {
    if (this.productId) {
      this.product = await this.apiProducts.get(this.productId)
    }

    this.debouncedSave = debounce((showAlert) => {
      this.saveProduct(showAlert)
    }, 2000, false)
  },

  methods: {
    advanceTab () {
      const next = (this.$refs[`tab-${this.activeTab + 1}`] || [])[0]
      if (!next) return

      next.$el.click()
    },

    scrollToCardTop () {
      const top = document.querySelector('.product-card')
      if (!top) return

      top.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },

    changeField ({ field, value, index }) {
      const skippedFields = ['logo']

      if (index >= 0) {
        this.product[field].splice(index, 1, value)
      } else {
        this.product[field] = value
      }

      if (!skippedFields.includes(field)) {
        this.productUpdated = true
        this.updatedProductFields[field] = this.product[field]
      }

      if (field === 'dealSize') {
        this.updatedProductFields.dealSize = {
          min: parseInt(((value.min || '') + '').replace(/,/g, ''), 10),
          max: parseInt(((value.max || '') + '').replace(/,/g, ''), 10),
        }
      }

      this.debouncedSave(false)
    },

    updateInternalName (name) {
      this.changeField({ field: 'internalName', value: name })
      this.showInternalNameDialog = false
    },

    async saveProduct (alert = true) {
      this.saving = true

      try {
        if (this.productUpdated) {
          await this.apiProducts.patch(this.productId, this.updatedProductFields)
          this.productUpdated = false
          this.updatedProductFields = {}
        }

        if (alert) {
          this.alertMessage.type = 'success'
          this.alertMessage.list = []
          this.alertMessage.message = 'Loan Product changes were updated'
        }
      } catch (error) {
        if (alert) {
          this.addErrorAlert(`Failed to save your changes: ${error.message}`)
        }
      }

      this.saving = false
    },

    async updatePublishedStatus (published = false) {
      try {
        if (published) {
          await this.apiProducts.patch(this.productId, this.updatedProductFields)
          this.productUpdated = false
          this.updatedProductFields = {}
        }

        await this.apiProducts.patch(this.productId, { published })
        this.product.published = published

        this.alertMessage.type = 'success'
        this.alertMessage.list = []
        this.alertMessage.message = published
          ? `${this.product.internalName || this.product.title} is now live!`
          : `${this.product.internalName || this.product.title} is now private!`
        document.querySelector('.alert-wrapper').scrollIntoView({ behavior: 'smooth' })
      } catch (error) {
        this.alertMessage.type = 'error'

        if (error.errors && error.errors.length) {
          this.alertMessage.message = `${this.product.internalName || this.product.title} could not be published. Please complete:`
          this.alertMessage.list = error.errors
        } else {
          this.alertMessage.message = `There was an error changing the publish status: ${error.message}`
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.v-card__title {
  display: grid;
  grid-template-columns: 1fr;
  grid-row-gap: 20px;
  align-items: center;
  background-color: $accentBlue;

  @include lg {
    grid-template-columns: 280px 1fr 280px;
    grid-auto-flow: column;

    h2 {
      @include gridPos($colS: 2, $colE: 3);
    }

    .button-group {
      @include gridPos($colS: 3, $colE: 4);
    }
  }

  h2 {
    display: flex;
    align-items: center;
    justify-content: center;

    color: white !important;
    font-family: "Sansation Bold", sans-serif;
    font-size: 18px;
  }

  .v-icon {
    margin-left: 10px;
    cursor: pointer;

    color: white;
    font-size: 18px;
  }

  .button-group {
    align-self: flex-end;
    justify-content: flex-end;
  }
}

.v-card__text {
  padding-left: 20px;
  padding-right: 20px;
}

.v-card__actions {
  padding-top: 30px;
  padding-bottom: 50px;
}

.alert-wrapper {
  display: flex;
  justify-content: center;
  margin-bottom: 20px;

  .v-alert {
    padding: 10px 10px;
    min-width: 250px;

    font-family: "Sansation Bold", sans-serif;
    font-size: 18px;
    font-weight: bold;

    &.success--text {
      background-color: #DEFFE6 !important;
      color: #0A6F21 !important;

      .v-icon { color: #0A6F21; }
    }

    &.error--text {
      background-color: #F2E4E4 !important;
      color: #C32020 !important;

      .v-icon { color: #C32020; }
    }

    ul {
      margin-top: 5px;
      padding-left: 10px;

      li {
        margin-bottom: 0;
        line-height: 20px;
      }
    }
  }

  ::v-deep {
    &.v-alert__wrapper {
      align-items: flex-start;
    }
  }
}

</style>
